import { useState } from 'react'
import { Button, Card, IconButton, Input, Person } from '../../../design/components'
import { useDebounced } from '../../../lib/useDebounced'
import { AVAILABLE_USER_MIN_SEARCH, useAvailableUsers } from '../queries'
import type { PlayersSection } from '../types'
import type { CampaignEditing } from '../useCampaignEditing'
import styles from './sections.module.css'

/**
 * Who is at the table. Section `players`.
 *
 * The Game Master is in this list, flagged — the server folds them in, because they
 * are a column on the campaign rather than a membership row, and no client should
 * have to remember that. They are also the one participant with no Remove control:
 * losing the GM is a delete or a hand-over, not a membership change, and the API
 * rejects it.
 */
export function PlayersCard({
  players,
  editing,
  campaignId,
}: {
  players: PlayersSection | undefined
  editing: CampaignEditing
  campaignId: number
}) {
  const [inviting, setInviting] = useState(false)

  if (players === undefined) return null

  const participants = players.participants ?? []
  const canManage = editing.access.isGameMaster

  return (
    <Card
      variant="fate"
      label="Players"
      hint={`${participants.length} at the table`}
      action={
        canManage && !inviting ? (
          <IconButton label="Invite a player" icon="add" onClick={() => setInviting(true)} />
        ) : undefined
      }
    >
      {participants.length === 0 && <p className={styles.empty}>Nobody here yet.</p>}

      {participants.map((participant) => (
        <div key={participant.userId} className={styles.row}>
          <div className={styles.rowHead}>
            <Person
              name={participant.displayName ?? 'Unnamed user'}
              meta={participant.isGameMaster ? 'Game Master' : 'Player'}
            />
            {canManage && !participant.isGameMaster && (
              <Button
                variant="danger"
                disabled={editing.removePlayer.isPending}
                onClick={() => editing.removePlayer.mutate({ userId: participant.userId })}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      ))}

      {canManage && inviting && (
        <InviteForm campaignId={campaignId} editing={editing} onClose={() => setInviting(false)} />
      )}
    </Card>
  )
}

/**
 * Invite by search.
 *
 * There is no "list all users" endpoint and deliberately so — `available-users` is
 * the one call that reaches outside the campaign, and it requires a term of at least
 * two characters rather than enumerating the user table. The form says that up front
 * instead of letting an empty box look broken.
 */
function InviteForm({
  campaignId,
  editing,
  onClose,
}: {
  campaignId: number
  editing: CampaignEditing
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search)
  const candidates = useAvailableUsers(campaignId, debounced)

  const tooShort = debounced.trim().length < AVAILABLE_USER_MIN_SEARCH
  const found = candidates.data ?? []

  return (
    <div className={styles.inlineForm}>
      <Input
        label="Find a user"
        type="search"
        placeholder="Name or login…"
        autoFocus
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {tooShort && <p className={styles.note}>Type at least two characters to search.</p>}

      {!tooShort && candidates.isPending && <p className={styles.note}>Searching…</p>}

      {!tooShort && candidates.isSuccess && found.length === 0 && (
        <p className={styles.note}>Nobody outside this campaign matches that.</p>
      )}

      <div className={styles.pickerList}>
        {found.map((user) => (
          <button
            key={user.userId}
            type="button"
            className={styles.pickerRow}
            disabled={editing.addPlayer.isPending}
            onClick={() =>
              editing.addPlayer.mutate({ userId: user.userId ?? '' }, { onSuccess: onClose })
            }
          >
            <Person name={user.displayName ?? 'Unnamed user'} />
          </button>
        ))}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
